import {BASIS, IEltInitableAsync, MsgLabel} from "back/commons/basis";
import {Actionables} from "back/commons/widgets/bars";
import {POPUP} from "back/commons/widgets/popups";
import {LoggedUser} from "back/core/widgets/loggedUser";
import {AccelKeyMgr, Action, ACTION, IAccelKeyMgrPointer} from "lib/commons/actions";
import {Area} from "lib/commons/areas";
import {Desk, UrlHashObjDeskFeat} from "lib/commons/desk";
import {ILastDatasBuilder, ILastDatasHolder, JLastDatas, LASTDATAS,} from "lib/commons/lastDatas";
import {IReg, IRegPointer, IUiEnv, REG} from 'lib/commons/registry';
import {IView, VIEWS} from "lib/commons/views";
import {JSX, ShadowJsx} from "lib/commons/xml/dom";
import {LastDatasDeskFeat} from "lib/core/lastDatas";
import {BasicUniverse, IBasicUniversePointer, IMaintenanceCb, IUniverseEnv, IWsExecFrame} from "lib/core/universe";
import {AuthSrv, JUser} from "lib/core/user";
import {ERROR} from "lib/core/errorReport";
import {CDM} from "lib/commons/utils/cdm";
import {DOMSH} from "lib/commons/xml/domsh";
import {IContextMenuActionsPointer} from "back/commons/actionables";
import {IPopupable, OPopupAnchor} from "back/commons/widgets/popupable";
import {GFX} from "lib/commons/utils/gfx";
import {Button, ButtonToggle, OButtonInit} from "back/commons/widgets/buttons";
import "back/core/zones/webZones";

/**
 * Constructeur d'apps.
 */
export interface IAppBuilder<ENVIN, ENVOUT> extends Area<IAppCtx<ENVIN>, IApp<ENVOUT>> {

	/**
	 * Cet appBuilder peut-il fournir une app avec cette définition (généralement json issu du hash d'une url).
	 */
	isAppDefMatch(data: JAppDef): boolean

	/**
	 * retourne les params au juste nécessaire
	 * Remarque : modifie l'objet passé en paramètre
	 */
	normalizeAppDef?(data: JAppDef): void

	/**
	 * Construit une app.
	 * Note : une IApp ne doit pas dépendre de son contexte DOM parent (repositionnement libre)
	 */
	buildBody(ctx: IAppCtx<ENVIN>, lastDatas?: JLastDatas): IApp<ENVOUT>

	loadBody(ctx: IAppCtx<ENVIN>, lastDatas?: JLastDatas): Promise<IApp<ENVOUT>>

}

/** Contexte pour la construction d'une App. */
export interface IAppCtx<ENVIN> extends IRegPointer<ENVIN> {

	/** Données issues du hash de l'URL ou d'une autre source pour créer cette app. */
	appDef: JAppDef

	/** Donnée de restauration de la app selon son dernier état enregistré. */
	lastDatas?: JLastDatas

	/**
	 * Informe l'app à sa construction qu'elle dispose d'une barre de titre riche
	 * qu'elle pourra exploiter lorsque le contexte appellera IApp.getHeadTitleBar()
	 * (juste après son initialisation). Au cours de la vie d'une même app IApp.getHeadTitleBar()
	 * pourra être appelé plusieurs fois et peut retourner toujours le même élément.
	 * La app peut garder une ref à cet élément pour des mises à jour dynamiques.
	 */
	hasHeadTitleBar?: boolean
}

/**
 * Propriétés json de définition d'une app. Ces données permettent
 * - d'identifier la app à construire. Exemple app=wsp, app=dynGen, app=editDepot, app=userAdmin...
 * - de fournir les données minimale pour reconstruire une vue équivalente dans une nouvelle
 *   fenetre. Exemple pour une app d'affichage
 *     - d'un atelier chain : wspRef.
 *     - d'un dynGen sur un item : wspRef + genCode + skin
 *
 * Ces propriétés ne contiennent pas l'URL du serveur (univers) ni du user courant qui relèvent de l'appFrame.
 */
export type JAppDef = {
	/** app fallback appelée en cas d'impossibilité d'afficher cette app
	 * Note : exploité uniquement lors du traitement de l'URL du hash
	 */
	fbApp?: JAppDef
}

/**
 * Représente une App instanciée sous forme d'un Custom Element DOM.
 */
export interface IApp<ENV> extends IView, ILastDatasBuilder, IRegPointer<ENV & IUiEnv>, IEltInitableAsync, IAccelKeyMgrPointer<IApp<ENV>> {

	/** Raison de l'échec de l'app
	 * Note : renseigné SSi l'app est chargée */
	appErrorStatus?: 'accessDenied' | string

	/** Si cette app peut-etre affichée sans user connecté. */
	anonymousAllowed?: boolean

	/**
	 * Définition de l'état courant de la app. Peut-être utilisé pour construire le hash d'une url
	 * et réobtenir cette app avec les mêmes principales données chargées.
	 */
	readonly appDef: JAppDef

	/**
	 * Met à jour la définition de cette app du fait d'un event extérieur, par exemple dû à une
	 * modification de hash de l'URL.
	 *
	 * Inversement, si la app met à jour sa propre définition, un DOM event "c-appdef-change" (bubbles=true)
	 * doit être généré pour notifier l'appFrame. Si la app ne peut revenir dans l'état précédent, une
	 * propriété 'revertable' doit être mise à false dans les CustomEvent.detail : {bubbles: true, detail: {revertable:false}}
	 *
	 * @return true si cette app est toujours valide avec cette nouvelle définition, false si l'appFrame
	 * appelant devrait repasser par ses IAppBuilder pour remplacement de l'app.
	 */
	updateAppDef(def: JAppDef): boolean


	/**
	 * Offre la possibilité à l'app d'injecter une structure html plus riche que son simple titre IApp.getLabel().
	 * Exemple : ajout d'un menu d'options, etc.
	 * Ce contenu fournit par la App doit se conformer à un espace de type barre de titre.
	 * Cette méthode sera appelée par le contexte applicatif (si il dispose de cette possbilité) ; à l'init
	 * de la app, elle saura qu'elle aura cette possibilité via la propriété IAppCtx.hasHeadTitleBar=true.
	 */
	getHeadTitleBar?(): HTMLElement

	/**
	 * Offre la possibilité à l'app de capter des events claviers globaux au niveau de la window,
	 * (même si la app ne détient pas le focus) si le container le permet
	 */
	accelKeyMgr?: AccelKeyMgr<IApp<ENV>>

	// /** Entête standard d'une app, optionnel */
	// readonly appHeader?: AppHeader<IApp<ENV>>
}

export function isApp<ENV>(tag: HTMLElement): tag is IApp<ENV> {return tag && ('appDef' in tag)}

/**
 * Feature du Desk pour constituer un cadre graphique pour UNE SEULE app simultannée.
 * Se constitue de :
 * - 1 header (toolbar : appframe:header:toolbar)
 * - 1 zone main à populer
 * - 1 footer : zone info, user
 *
 * Gère l'authentification initiale de la page fondé sur REG.reg.env.universe.
 * Un menu "Options" ajouté par défaut en fin de toolbar du header (masqué automatiqument si vide).
 *
 * TODO dériver un AppFrameMultiDeskFeat pour un concept de bureau avec plusieurs apps lancées simultannément et une barre en bas...
 *
 */
export class AppFrameDeskFeat extends Desk {

	static add(desk: Desk, options?: OAppFrameDeskFeatOptions & OAppFrameOptions): AppFrameDeskFeat {
		if (this.isIn(desk)) return desk;
		LastDatasDeskFeat.add(desk);
		ContextMenuDeskFeat.add(desk);
		desk.assignProps(this);
		const d = desk as AppFrameDeskFeat & LastDatasDeskFeat;
		d._appBuilders = [];
		d.initialTitle = document.title;

		//Construction d'un id de session unique dans le navigateur.
		d.ldSession = location.search.substr(1);
		const bcSessions = new BroadcastChannel(location.pathname);
		const rand = Math.random();
		bcSessions.onmessage = (ev: MessageEvent) => {
			if (ev.data.req === "getSessions") {
				//un autre onglet demande les sessions en cours, on lui répond.
				bcSessions.postMessage({resp: 'getSessions', session: d.ldSession, r: ev.data.r});
			} else if (ev.data.resp === "getSessions" && ev.data.r === rand) {
				//réponse à notre requête.
				if (ev.data.session === d.ldSession) d.onLdSessionConflict();
			}
		};
		bcSessions.postMessage({req: 'getSessions', r: rand});

		//Bloque tout drop natif dans la page.
		document.ondragenter = document.ondragover = document.ondrop = (ev: DragEvent) => {
			ev.dataTransfer.dropEffect = 'none';
			ev.preventDefault();
		};

		//Le plus tôt possible : chargement du user authentifié courant
		REG.reg.addToList(Desk.LC_init, 'loadUser', 1, () => {
			d._auth = (REG.reg.env as IBasicUniversePointer).universe.auth;
			return d._auth.fetchUser();
		}, -1000);

		//En fin de chargement : construction de l'ihm root (remplaçant l'ihm d'attente)
		REG.reg.addToList(Desk.LC_load, 'buildUi', 1, () => {
			const body = document.body as HTMLElement & ILastDatasBuilder & ILastDatasHolder;

			//LastDatas
			body.buildLastDatas = (p: JLDAppFrame) => {
				if (d._app) {
					p.appDef = d._app.appDef;
					p.appLD = LASTDATAS.buildLastDatas({}, d._app, true);
				}
			};
			body.getParentLastDatas = (forChild: HTMLElement): JLastDatas | null => {
				const ld = (desk as LastDatasDeskFeat).lastDatas as JLDAppFrame;
				return ld ? ld.appLD : null;
			};

			//Construction du body
			body.textContent = null;
			REG.reg.installSkin('appFrame');

			const actions = <Actionables id="headerActions" actions="appframe:header:toolbar" group-order="*" ui-context="dialog"/>;
			const portalTi = <span id="portalTi">{document.title}</span>;
			const collapseActions = <ButtonToggle id="collapseActions" title="Masquer" icon="/@skin@/core/widgets/appFrame/collapse.svg" onclick={function (this: ButtonToggle) {
				this.toggleOn = portalTi.hidden = actions.hidden = !this.toggleOn;
				this.title = this.toggleOn ? "Afficher" : "Masquer";
			}}/>;
			d.appBar = <span id="appBar"/>;
			d.mainBar = <div id="mainBar">
				<ShadowJsx skin="appFrame/mainBar" skinOver="webzone:invert">
					<Button id="home" onclick={() => {(desk as AppFrameDeskFeat).gotoHome()}}>
						<div id="logo"><img src={options && options.iconUrl || "/@skin@/core/logoSC.svg"} title={options.appName}/></div>
						{portalTi}
					</Button>
					{actions}
					{collapseActions}
				</ShadowJsx>
			</div>;
			d.header = body.appendChild(
				REG.reg.getSvc('appFrameHeader') as HTMLElement ||
		  <header>
						{d.mainBar}
						{d.appBar}
			  <div id="loggedUserBox"><LoggedUser î={{icon: "--user-icon", reg: (options && options.authReg) || REG.reg}}/></div>
		  </header>
			) as HTMLElement;

			//body.appendChild(<hr/>);

			d.mainCtn = body.appendChild(REG.reg.getSvc('appFrameMain') as Element || <main/>) as HTMLElement;
			d.mainCtn.addEventListener("c-view-change", function (this: HTMLElement, ev: CustomEvent) {
				if ((ev.target as Element).parentElement === this) (desk as AppFrameDeskFeat).refreshTitle();
			})
			d.mainCtn.addEventListener("c-appdef-change", function (this: HTMLElement, ev: CustomEvent) {
				if ((ev.target as Element).parentElement === this) (desk as AppFrameDeskFeat).updateUrlHash(ev.detail && ev.detail.revertable === false);
			});

			//Masquage du header en mode fullscreen
			if (document.fullscreenEnabled) {
				d.header.append(<Button id="fullscreen" î={{reg: REG.reg, icon: "/@skin@/core/actions/portalbar/fullscreen.svg", title: "Contenu en plein écran"} as OButtonInit} onclick={AppFrameDeskFeat.requestFullscreen}/>);
			}

			if (UrlHashObjDeskFeat.isIn(d)) d.onUrlHashChange.add(() => d.setMainAppFromHash());

			//On tue le racourcis ctrl+S du navigateur qui n'a pas de sens dans un appFrame.
			REG.reg.addToList(Desk.LIST_accelKeys, "s-accel", 1, new Action<any>('killCtrlS').setExecute(function (c, ev) {ev.preventDefault()}));
			REG.reg.addToList(Desk.LIST_accelKeys, "F1", 1, new Action<any>('killF1').setExecute(function (c, ev) {ev.preventDefault()}));

			d._auth.listeners.on('loggedUserChanged', async (oldUser: JUser, newUser: JUser) => {
				//on clos l'app actuelle
				await d.closeMainApp(true);

				if (oldUser && !newUser) {
					//Déconnexion, on efface l'état courant dans le hash
					if (UrlHashObjDeskFeat.isIn(d)) await d.setUrlHash(null);
				}

				if (newUser) {
					//on recharge à partir du hash et des (nouveaux) lastDatas
					await d.setMainAppFromHash((desk as LastDatasDeskFeat).lastDatas);
				} else {
					// Plus de user car anonymous interdit.
					// on force l'affichage du login.
					const loginAct = REG.reg.getSvc('userLogin') as Action<Element>;
					loginAct.execute(body);
				}
				//On refresh les bars.

				BASIS.refreshTree(d.mainBar.shadowRoot);
				BASIS.refreshTree(d.header);
				//if (d.footer) BASIS.refreshTree(d.footer);
			}, 100); //La reconstruction de la page devrait intervenir en dernier, après les rechargements, lastDaats, userDatas...

			/** On controle qu'on peut clore l'app courante avant logOut. */
			d._auth.listeners.on('beforeLogout', (user: JUser): void | 'stop' | Promise<void | 'stop'> => {
				if (d._app) return VIEWS.canHideView(d._app).then((canClose: boolean) => (canClose ? undefined : 'stop') as void | 'stop');
			});

			if (!d._auth.config.anonymousUser) {
				//Si anoymous pas autorisé, affichage de l'écran de log au chargement de la page.
				if (d._auth.currentUser == null) {
					const loginAct = REG.reg.getSvc('userLogin') as Action<Element>;
					loginAct.execute(body);
				}
			}

			//Lancement de la app si forcé
			if (d._app) d.setMainApp(d._app);
			//Sinon on tente de la lancer à partir du hash et des lastDatas.
			else d.setMainAppFromHash((desk as LastDatasDeskFeat).lastDatas);
		}, 1000);

		//Gestion des erreurs de connexion réseau
		REG.reg.addToList(Desk.LC_load, 'netError', 1, () => {
			const universe = (REG.reg.env as IBasicUniversePointer).universe;
			const url = new URL(universe.httpFrames.web.config.execFrameUrl.url);
			if (url.hostname !== 'localhost' && url.hostname !== '127.0.0.1') {
				d.isServerLocal = false;
				//Par l'écoute de l'evt "offline" on détecte une coupure réseau.
				window.addEventListener('offline', refreshOffline);
				window.addEventListener('online', refreshOffline);
				refreshOffline();
			} else d.isServerLocal = true;
		}, 1010);
		return d;
	}

	static isIn(desk: Desk): desk is AppFrameDeskFeat {return 'setMainAppFromHash' in desk}

	protected onGloablKeyDown(this: void, ev: KeyboardEvent) {
		if (window.desk.accelKeys.handleKeyboardEvent(ev, window) === 0) {
			(window.desk as AppFrameDeskFeat)._app?.accelKeyMgr?.handleKeyboardEvent(ev, (window.desk as AppFrameDeskFeat)._app);
		}
	}

	/**
	 * Ajout d'un appBuilder dans cet appFrame
	 */
	addAppBuilder(appBuilder: IAppBuilder<any, any>) {
		this._appBuilders.push(appBuilder);
	}

	/**
	 * App construite par défaut, si aucune autre app disponible.
	 * Initialisé avec le REG.reg racine et l'appDef issu du hash
	 * pour indiquer une éventuelle erreur dans le hash.
	 */
	setDefaultAppBuilder(appBuilder: IAppBuilder<any, any>) {
		this._defaultAppBuilder = appBuilder;
	}

	/**
	 * Gestionnaire de conflit d'id de session.
	 * La version actuelle génère un id aléatoire, un prompt au user pourrait aussi être envisagé.
	 */
	onLdSessionConflict() {
		const s = (desk as LastDatasDeskFeat).ldSession = (Math.round(Math.random() * 9999999)).toString(36);
		//On ajoute un ~ pour repérer que c'est une session random, donc un code non signifiant
		//pour le user, à remplacer par un n° dans l'ihm de gestion des sessions.
		location.search = "?~" + s;
	}

	/** Surveillance d'une connexion webSocket d'un univers pour notifier une iterruption d'accès au serveur. */
	surveyWsConnexion(ws: IWsExecFrame) {
		//Par l'écoute de la connection WS, on détecte l'arret du serveur.
		ws.lcListeners.on("closed", function (code: number) {
			if (code !== 1000) (desk as AppFrameDeskFeat).setServerOff(true);
			else {
				//cloture sur user inactif.
				//plus de ws pour détecter un arrêt serveur, on écoute les erreurs de connection ?
				//XXX écouter les erreurs fetch ? window.addEventListener('error', onWindowError);
			}
		});
		ws.lcListeners.on("opened", function () {
			//XXX écouter les erreurs fetch ? window.removeEventListener('error', onWindowError);
			(desk as AppFrameDeskFeat).setServerOff(false);
		});
		ws.msgListeners.on("liaise", this.onLiaiseMsg);
	}

	onLiaiseMsg(this: void, m: JMaintenanceMsg) {
		//console.log("liaise:::::::::::", m);
		if (m.type !== "maintenance") return;
		if (!m.msg && m.opType) {
			//COMPAT XUL
			switch (m.opType) {
			case "update" :
				m.msg = "ATTENTION, une mise à jour du serveur est imminente";
				break;
			case "restart":
				m.msg = "ATTENTION, un redémarrage du serveur est imminent";
				break;
			case "shutdown":
				m.msg = "ATTENTION, l'arrêt du serveur est imminent";
				break;
			}
		}
		(desk as AppFrameDeskFeat).setMaintenanceMsg(m.msg, m.until);
	}

	/** Détection de l'état du serveur. */
	isServerOff: boolean;

	/** Le serveur est-il sur le net ou local (localhost). */
	isServerLocal: boolean;

	setServerOff(off: boolean) {
		this.isServerOff = off;
		this.maintenanceMsg = null; //pour ne pas afficher un msg si relancé plus tot que le until.
		this.refreshErr();
	}

	maintenanceMsg?: string;
	maintenanceMsgUntil?: number;

	/** Affiche un message de maintenance. */
	setMaintenanceMsg(msg: string, until?: number) {
		const now = Date.now();
		if (!until) until = now + 30000; //msg affiché 30s par défaut.
		const delay = until - now;
		if (delay > 500) {
			this.maintenanceMsgUntil = until;
			this.maintenanceMsg = msg;
			this.refreshErr();
			setTimeout(() => {
				if (this.maintenanceMsg === msg) {
					//msg pas modifié entre temps, on reset
					this.maintenanceMsg = null;
					this.refreshErr();
				}
			}, delay);
		}
	}

	refreshErr() {
		if (this.isServerOff) {
			this.setHeadband("Votre connexion au serveur est interrompue ou instable", "error");
		} else if (!this.isServerLocal && !navigator.onLine) {
			this.setHeadband("Vous n'êtes pas connecté·e au réseau, accès impossible au serveur.", "error");
		} else if (this.maintenanceMsg) {
			this.setHeadband(this.maintenanceMsg, "warning");
		} else {
			this.setHeadband(null);
		}
	}

	setHeadband(msg: string, level?: 'info' | 'error' | 'warning', icon?: string) {
		if (msg) {
			if (!this._headband) this._headband = <MsgLabel class="headband"/> as MsgLabel;
			if (!this._headband.isConnected) document.body.insertBefore(this._headband, this.mainCtn);
			this._headband.setCustomMsg(msg, level, icon);
		} else {
			if (this._headband && this._headband.isConnected) this._headband.remove();
		}
	}

	protected _headband: MsgLabel;

	/** */
	async setMainAppDef(appDef: JAppDef): Promise<boolean> {
		for (const bd of this._appBuilders) {
			if (bd.isAppDefMatch(appDef)) {
				return this.setMainApp(await bd.loadBody({reg: REG.reg, appDef, hasHeadTitleBar: true}));
			}
		}
		return false;
	}

	async setMainAppFromHash(useLD?: JLDAppFrame): Promise<void> {
		if (this._updtHashFromHere) return; //bloque réentrance quand la modif vient de nous.
		const appDef = UrlHashObjDeskFeat.isIn(this) ? this.getUrlHash() : useLD && useLD.appDef;
		try {
			if (appDef) {
				//L'app actuelle peut-elle prendre en charge cet appDef.
				if (this._app && this._app.updateAppDef(appDef)) return;
				//On cherche un appBuilder compatible
				for (const bd of this._appBuilders) {
					if (bd.isAppDefMatch(appDef)) {
						if (!await this.setMainApp(await bd.loadBody({
							reg: REG.reg,
							appDef,
							lastDatas: useLD && useLD.appDef && bd.isAppDefMatch(useLD.appDef) ? useLD.appLD : null,
							hasHeadTitleBar: true
						}))) {
							let fbApp = appDef.fbApp;
							while (fbApp) {
								if (await this.setMainAppDef(fbApp))
									return;
								fbApp = fbApp.fbApp;
							}
							throw "";
						}
						return;
					}
				}
				if (useLD && useLD.appDef) {
					//Pas trouvé, on recherche la app des lastDatas
					for (const bd of this._appBuilders) {
						if (bd.isAppDefMatch(useLD.appDef)) {
							if (!await this.setMainApp(await bd.loadBody({
								reg: REG.reg,
								appDef: useLD.appDef,
								lastDatas: useLD.appLD,
								hasHeadTitleBar: true
							}))) throw "";
							return;
						}
					}
				}
			}
			//Rien trouvé, on tente la defaultAppBuilder.
			if (this._defaultAppBuilder) {
				if (!await this.setMainApp(await this._defaultAppBuilder.loadBody({
					reg: REG.reg,
					appDef: appDef || {},
					hasHeadTitleBar: true
				}))) throw "";
			} else {
				if (await this.closeMainApp() === false) throw "";
			}
		} catch (e) {
			//Echec à la mise à jour de l'app => on restaure le précédent hash
			await ERROR.log("Failed to load app", e);
			this.updateUrlHash();
		}
	}

	/**
	 * Méthode publique commune à Tout AppFrameXxxDeskFeat.
	 * Permet d'adapter l'ouverture de l'app en fonction de l'AppFrame.
	 *
	 * @param options Options d'affichage
	 */
	async findAndOpenApp(appDef: JAppDef, ev?: Event, options?: any): Promise<boolean> {
		for (const bd of this._appBuilders) {
			if (bd.isAppDefMatch(appDef)) {
				const nmAppDef: JAppDef = {};
				Object.assign(nmAppDef, appDef);
				if ("normalizeAppDef" in bd) bd.normalizeAppDef(nmAppDef);
				if ((ev instanceof MouseEvent || ev instanceof KeyboardEvent) && ACTION.isAccelPressed(ev)) {
					const url = new URL(document.location.href);
					url.hash = CDM.stringify(nmAppDef);
					window.open(url.href, "_blank");
					return true;
				}
				await this.setMainApp(await bd.loadBody({reg: REG.reg, appDef: nmAppDef, hasHeadTitleBar: true}));
				return true;
			}
		}
		return false;
	}

	/**
	 * Méthode publique commune à Tout AppFrameXxxDeskFeat.
	 *
	 * @param options Options d'affichage
	 */
	async openApp(appBuilder: IAppBuilder<any, any>, appDef: JAppDef, options?: any) {
		await this.setMainApp(await appBuilder.loadBody({reg: REG.reg, appDef, hasHeadTitleBar: true}));
	}

	protected fAppIniting: Promise<boolean>;

	protected async setMainApp<ENV>(app: IApp<ENV>): Promise<boolean> {
		if (this.fAppIniting) this.fAppIniting = (this.fAppIniting as any).finally(() => this.fAppIniting = this.setMainAppChained(app));
		else this.fAppIniting = this.setMainAppChained(app);
		return this.fAppIniting;
	}

	protected async setMainAppChained<ENV>(app: IApp<ENV>): Promise<boolean> {
		if (this.mainCtn && (this._auth.currentUser || app.anonymousAllowed)) {
			const oldApp = this._app;
			if (oldApp && await (oldApp === app ? this.hideMainApp() : this.closeMainApp()) === false) return false;
			this._app = this.mainCtn.appendChild(app);
			VIEWS.onViewShown(app);
			await this._app.initializedAsync;
			this.refreshTitle();
			this.updateUrlHash();
			if (this._app !== app || this._app.appErrorStatus != null) return false;
		} else {
			this._app = app; //en attente...
		}
		return true;
	}

	async gotoHome(): Promise<boolean> {
		if (this._defaultAppBuilder) {
			return this.setMainApp(await this._defaultAppBuilder.loadBody({reg: REG.reg, appDef: {}, hasHeadTitleBar: true}));
		} else {
			const ok = await this.closeMainApp();
			if (ok) this.updateUrlHash();
			return ok;
		}
	}

	/**
	 * @param replace Pas d'historique. cf https://developer.mozilla.org/en-US/docs/Web/API/Location/replace
	 */
	protected updateUrlHash(replace?: boolean) {
		if (UrlHashObjDeskFeat.isIn(desk)) {
			try {
				this._updtHashFromHere = true;
				desk.setUrlHash(this._app ? this._app.appDef : null, replace);
			} finally {
				this._updtHashFromHere = false;
			}
		}
	}

	protected _updtHashFromHere: boolean;

	isMainAppAvailable() {return this._app != null}

	/** Masque l'app courante SANS la clore. */
	async hideMainApp(force?: boolean): Promise<boolean | undefined> {
		const app = this._app;
		const wasAlive = app && app.isConnected;
		if (wasAlive && !force && !(await VIEWS.canHideView(app))) return false;
		if (!await POPUP.closeAllPopup(app)) return false;
		if (this._app !== app) return undefined; //race cond, autre app affectée entre temps.
		this.mainCtn.textContent = null;
		if (wasAlive) VIEWS.onViewHidden(app);
		return true;
	}

	/** Retourne l'app courante. */
	public getMainApp(): IApp<any> | null {
		return this._app;
	}

	/**
	 * Clos l'app courante. Attention, url non mise à jour.
	 * @return true si plus d'app courante. false si refus. undefined si race condition.
	 */
	protected async closeMainApp(force?: boolean): Promise<boolean | undefined> {
		const app = this._app;
		if (app) {
			try {
				if (!force && app.isConnected && !(await VIEWS.canHideView(app))) return false;
				if (!await POPUP.closeAllPopup(app)) return false;
				if (this._app !== app) return undefined; //race cond, autre app affectée entre temps.
				this._app = null;
				this.mainCtn.textContent = null;
				VIEWS.onViewHidden(app, true);
				this.refreshTitle();
			} catch (e) {
				//pour ne pas bloquer l'ouverture d'une autre app.
				console.log(e);
			}
		} else {
			this.mainCtn.textContent = null;
		}
		return true;
	}

	/**
	 * Recharge l'app courante (changement de contexte, de pref user...)
	 */
	async reloadMainApp(force?: boolean): Promise<boolean> {
		const appDef = this._app?.appDef;
		if (appDef && await this.closeMainApp(force)) return this.findAndOpenApp(appDef);
		return false;
	}

	showInfo(text: string) {
		// if (this.statusBar) {
		// 	this.statusBar.setStatus(text);
		// } else {
		POPUP.showNotif(text, document.body);
		//}
	}

	refreshTitle() {
		const appTitle = this._app ? VIEWS.getLabel(this._app) : null;
		document.title = appTitle ? appTitle + " - " + this.initialTitle : this.initialTitle;
		if (typeof this._app?.getHeadTitleBar === "function") {
			const bar = this._app.getHeadTitleBar();
			if (this.appBar.firstElementChild !== bar) {
				this.appBar.textContent = null;
				if (bar) this.appBar.appendChild(bar);
			}
		} else {
			this.appBar.textContent = null;
			this.appBar.append(<div class="appTitle">{appTitle}</div>);
		}
		if (Desk.electron) window.postMessage({type: "client:server:refreshTitle", appTitle, initialTitle: this.initialTitle}, location.origin);
	}

	static async requestFullscreen() {
		if (Desk.electron) {
			const exitButton = new Button().initialize({ uiContext: "dialog", label: "Quitter le mode plein-écran (Échap)" });
			const notif = POPUP.showNotifInfo(<p><span>L'application est désormais en mode plein écran.</span>{exitButton}</p>, document.body, null, {viewPortY: "top"});
			exitButton.addEventListener('click', async () => {
				notif.close();
				await document.exitFullscreen();
			}, { once: true });
		}
		await document.body.requestFullscreen();
	}

	initialTitle: string;

	protected header: HTMLElement;
	protected mainBar: HTMLElement;
	protected appBar: HTMLElement;
	protected mainCtn: HTMLElement;

	protected _appBuilders: IAppBuilder<any, any>[];
	protected _defaultAppBuilder: IAppBuilder<any, any>;

	protected _auth: AuthSrv;

	/** app courante (affichée ou non) */
	protected _app: IApp<any> | null;
}

function refreshOffline() {(window.desk as AppFrameDeskFeat).refreshErr()}

/** Options de AppFrameDeskFeat. */
interface OAppFrameDeskFeatOptions {
	iconUrl?: string,
	authReg: IReg<IUniverseEnv>
}

/** Options de AppDesk. */
interface OAppFrameOptions {
	appName?: string
}

interface JLDAppFrame {
	appDef: JAppDef
	appLD: JLastDatas
}

export interface JMaintenanceMsg {
	svc: "liaise"
	type: "maintenance"
	msg?: string
	until?: number
	opType?: "update" | "shutdown" | "restart" /* Compat XUL */
}


REG.reg.registerSkin('appFrame', 1, /* language=CSS */ `
	body {
		margin: 0;
		padding: 0;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		height: 100vh;
		overflow: hidden;
	}

	:focus-visible {
		outline: var(--focus-outline);
	}

	header {
		display: flex;
		min-height: auto;
		min-width: 0;
		flex-wrap: wrap;
		align-items: center;
		background-color: var(--page-bgcolor);
		box-shadow: 0 3px 3px rgba(0, 0, 0, 0.2);
		z-index: 1;
	}

	#appBar {
		flex: 1 0 auto;
		align-self: stretch;
		display: flex;
		min-width: 0;
		min-height: 0;
		align-items: center;
	}

	.appTitle {
		font-weight: bold;
		margin-inline: .3em;
		user-select: none;
	}

	#fullscreen {
		min-width: max-content;
		border: none;
		border-radius: 0;
		margin: 0 .3em;
		--bgcolor: transparent;
		align-self: stretch;
		font-size: 80%;
	}

	c-loggeduser {
		--label-size: 1rem;
		margin: 0 .3em;
		--user-icon: url('/@skin@/core/actions/portalbar/user.svg');
	}

	#loggedUserBox {
		display: flex;
		min-height: 0;
		min-width: 0;
		align-self: stretch;
	}

	.headband {
		white-space: pre;
	}

	main {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	body:fullscreen {
		background-color: var(--bgcolor);
	}

	body:fullscreen > header, body:fullscreen > hr {
		display: none;
	}
`);


REG.reg.registerSkin('appFrame/mainBar', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: auto;
		min-width: 0;
		flex-wrap: wrap;
		align-items: center;
		align-self: stretch;
	}

	:focus-visible {
		outline: var(--focus-outline);
	}

	[hidden] {
		display: none !important;
	}

	#home {
		align-self: stretch;
		font-size: 1rem;
	}

	#logo {
		flex: 0 0 auto;
		display: flex;
		min-width: fit-content;
		min-height: auto;
		align-items: center;
		margin-inline-end: .5em;
		padding: .1em;
		border-radius: .3em;
		background-color: #FFF;
	}

	#logo > img {
		max-height: 2em;
	}

	#portalTi {
		flex: 0 0 auto;
		display: flex;
		min-width: 0;
		min-height: 0;
		font-weight: bold;
	}

	#headerActions {
		display: flex;
		min-height: min-content;
		min-width: 0;
		flex-flow: wrap;
		align-self: stretch;
		border-inline: 1px solid var(--border-color);
	}

	#headerActions > c-action {
		min-width: max-content;
		border: none;
		border-radius: 0;
		margin: 0 .3em;
		align-self: stretch;
		font-size: 80%;
	}

	#collapseActions {
		align-self: stretch;
		padding: 0;
		border: none;
	}

  #collapseActions[aria-pressed="true"],
  #collapseActions:lang(ar) {
	  transform: scaleX(-1);
	  background-color: unset;
  }

  #collapseActions[aria-pressed="false"]:lang(ar) {
	  transform: none;
  }

  hr {
	  border-top: 1px solid var(--border-color);
	  border-inline-start: 1px solid var(--border-color);
	  border-inline-end: none;
	  border-bottom: none;
	  margin: .2rem;
	  align-self: stretch;
  }
`);

/**
 * Feature du desk pour le choix entre le menu contextuel custom (applicatif) vs le menu du navigateur.
 * main : le menu applicatif est affiché en 1er, le menu du navigateur en secondaire accessible via shift + click droit par ex.
 * secondary : le menu du navigateur est affiché en 1er
 */
export class ContextMenuDeskFeat extends Desk {

	static add(desk: Desk, mode?: "main" | "secondary", reg: IReg<IBasicUniversePointer> = REG.reg): ContextMenuDeskFeat {
		if (this.isIn(desk)) return desk;
		const d = desk as ContextMenuDeskFeat;
		desk.assignProps(this);
		d.setContextMenuCustom(mode);

		document.body.addEventListener(Desk.electron ? "c-contextmenu" : "contextmenu", d.showContextMenu);
		REG.reg.addToList(Desk.LIST_accelKeys, " -accel", 1, new Action<any>('contextMenu').setExecute(function (c, ev) {
			d.showContextMenu.call(document.body, ev);
		}));

		REG.reg.addToList(Desk.LC_init, 'contextMenu', 1, () => {
			//Recherche du AuthServer
			const univ = reg.env.universe;
			const auth = univ ? univ.auth : null;
			if (auth) {
				if (auth.currentAuthenticatedUser) d.setContextMenuCustom(reg.getUserData("contextMenu"));
				auth.listeners.on("loggedUserChanged", () => {
					d.setContextMenuCustom(reg.getUserData("contextMenu"));
				});
			}
		});

		// Désactivation du comportement par défaut du shift+clic droit qui augmente la sélection textuelle jusqu'au curseur et empêche la correction orthographique
		document.body.addEventListener("mousedown", (ev) => {
			if (ev.button == 2 && ev.shiftKey) ev.preventDefault();
		});
		return d;
	}

	static isIn(desk: Desk): desk is ContextMenuDeskFeat {return 'contextMenuCustom' in desk}

	contextMenuCustom: "main" | "secondary" = "main";

	setContextMenuCustom(mode?: "main" | "secondary") {
		this.contextMenuCustom = mode !== "secondary" || Desk.electron ? "main" : mode;
	}

	/** Appelé sur les events "contextmenu" (XXX à revoir : et "keydown"). */
	showContextMenu(this: HTMLElement, ev: MouseEvent & CustomEvent) {
		if (ev instanceof MouseEvent) {
			if ((desk as ContextMenuDeskFeat).contextMenuCustom === "main") {
				//On laisse le menu contextuel natif sur shift + clickDroit
				if (ev.shiftKey) return;
			} else {
				//On affiche notre menu que sur shift + clickDroit
				if (!ev.shiftKey) return;
			}
			ev.preventDefault();
		}
		let node = DOMSH.findDeepActiveElement(DOMSH.findDocumentOrShadowRoot(this));
		while (node && node !== this) {
			if ('ctxMenuActions' in node) {
				const menu = (node as IContextMenuActionsPointer<any>).ctxMenuActions;
				if (menu && menu.actions && menu.actions.length > 0) {
					let anchor: OPopupAnchor;
					if ('pageX' in ev && ev.which > 0/* bloque si menu contextuel par clavier*/) {
						anchor = {
							initX: menu.rect ? GFX.bound(menu.rect.left, ev.pageX, menu.rect.right) : ev.pageX,
							initY: menu.rect ? GFX.bound(menu.rect.top, ev.pageY, menu.rect.bottom) : ev.pageY
						};
					} else if (menu.rect) {
						anchor = {
							initX: menu.rect.left,
							initY: menu.rect.bottom
						}
					} else {
						const focused = DOMSH.findDeepActiveElement() || ev.target as Element;
						anchor = {
							posFrom: focused, intersectWith: DOMSH.findFlatParentElt(focused, null, (n: Node): n is HTMLElement => {
								//FIXME pas satisfaisant
								return n instanceof HTMLElement ? n.scrollHeight > n.clientHeight : false;
							})
						};
					}
					//console.log("here::::::::::::::::::", anchor, menu.rect, ev);
					const from = DOMSH.findDeepActiveElement() as HTMLElement;

					if (Desk.electron) {
						// Enrichissement du actionContext par les informations de corrections ortho dans l'event c-contextmenu spécifique à Electron
						if (ev.detail && ev.detail.misspelledWord) {
							menu.actionContext[ACTION.spellcheckSymbol] = {
								misspelledWord: ev.detail.misspelledWord,
								dictionarySuggestions: ev.detail.dictionarySuggestions,
							}
						} else {
							delete menu.actionContext[ACTION.spellcheckSymbol];
						}
					}

					return POPUP.showPopupActions({
						actions: menu.actions,
						actionContext: menu.actionContext,
						restoreFocus: from
					}, anchor, from || ev.target as Node);
				}
				return;
			}
			node = DOMSH.findParentElt(node);
		}
	}
}


/**
 * Ajoute la feature d'eval en amont du chargement de l'application si le navigateur implémente le standard Sec-Fetch-*.
 * Si le standard n'est pas implémenté:
 * - affiche un message d'erreur à l'utilisateur dans la page principale (comportement surchargeable),
 * - interrompt le processus d'init de la page.
 */
export class SecFetchDeskFeat extends Desk {

	/** Peut être ajouté après desk.launch(). */
	static add(desk: Desk): SecFetchDeskFeat {
		if (this.isIn(desk)) return desk;
		desk.assignProps(this);
		const d = desk as SecFetchDeskFeat;

		//En 1er : check de sécurité si le navigateur implémente Sec-Fetch-*
		REG.reg.addToList(Desk.LC_init, 'checkSecFetch', 1, async () => {
			let secFetchAware: boolean;
			try {
				secFetchAware = await d.checkSecFetchSiteAware();
			} catch (e) {
				//Accès au serveur impossible
				d.showErrorInfo(d.getNoNetworkMsg());
				//On interrompt le processus de téléchargement.
				throw e;
			}
			if (!secFetchAware) {
				d.showErrorInfo(d.getNoSecFetchMsg());
				//On interrompt le processus de téléchargement.
				throw Error("Sec-Fetch standard not available in this browser");
			}
		}, -100000);
		return d;
	}

	static isIn(desk: Desk): desk is SecFetchDeskFeat {return 'checkSecFetchSiteAware' in desk}

	async checkSecFetchSiteAware(): Promise<boolean> {
		return (REG.reg.env as IBasicUniversePointer).universe.isSecFetchSiteAware();
	}

	showErrorInfo(elt: HTMLElement) {
		document.body.textContent = null;
		document.body.append(elt);
	}

	getNoSecFetchMsg() {
		return <main>
			<h2>Votre navigateur est incompatible avec ce service</h2>
			<p>Votre navigateur n'assume pas une fonction nécessaire à la sécurité de ce service : <a href="https://w3c.github.io/webappsec-fetch-metadata">Fetch Metadata Request Headers</a></p>
			<p>Voici une liste de navigateurs connus qui dans leurs dernières versions sont compatibles avec ce service :</p>
			<ul>
				{Desk.knownNavCompat.map((v) => v.url ? <li><a href={v.url}>{v.name}</a></li> : <li>{v.name}</li>)}
			</ul>
		</main>;
	}

	getNoNetworkMsg() {
		return <main>
			<h2>Serveur inaccessible</h2>
			<p>Le serveur ne semble pas accessible. Vérifiez votre connexion et essayez ultérieurement.</p>
		</main>;
	}
}

/** IHM par défaut en cas d'opération de maintenance en cours des serveurs. */
let currentMaintenanceDialog: IPopupable;
REG.reg.registerSvc("maintenanceCb", 1, function (universe: BasicUniverse, msg: { lang: string, label: string }[]) {
	if (!currentMaintenanceDialog) {
		currentMaintenanceDialog = POPUP.showDialog(<section style="margin:.5em">
			<p>{msg[0].label}</p>
			<Button î={{label: "Fermer", uiContext: "dialog"} as OButtonInit} onclick={function (this: Button) {POPUP.findPopupableParent(this).close()}}/>
		</section>, document.body, {titleBar: {barLabel: {label: "Opérations de maintenance en cours"}}});
		currentMaintenanceDialog.onNextClose().then(() => {
			currentMaintenanceDialog = null;
		});
	}
} as IMaintenanceCb);